import { Component, ErrorInfo, ReactNode } from "react";
import { captureException } from "@/lib/observability";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  scope?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * React error boundary. Catches render-time crashes, reports to Sentry (when
 * configured), and renders a user-facing recovery UI instead of a white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    captureException(error, { componentStack: info.componentStack, scope: this.props.scope });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4 p-8 text-center">
        <h2 className="text-2xl font-semibold text-foreground">Something went wrong</h2>
        <p className="text-muted-foreground max-w-md">
          Our team has been notified. You can try again or return to the homepage.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={this.handleReset}>Try again</Button>
          <Button onClick={() => (window.location.href = "/")}>Go home</Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;